import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LogOut, RefreshCw, Search, Filter, ChevronDown, ArrowLeft, Trash2,
  RotateCcw, AlertCircle, Clock, Calendar, Phone, Mail, User, ChevronLeft, ChevronRight,
} from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const PAGE_SIZE = 10;
const RETENTION_DAYS = 30;

const getStatusColor = (status) => ({
  pending: 'bg-yellow-100 text-yellow-800',
  contacted: 'bg-blue-100 text-blue-800',
  enrolled: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
}[status] || 'bg-gray-100 text-gray-800');

const AdminTrashPage = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [dialog, setDialog] = useState(null); // { type, ids }
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchTrash = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API_URL}/api/admin/trash`, { credentials: 'include' });
      if (res.status === 401) { navigate('/admin'); return; }
      if (!res.ok) throw new Error('fail');
      setRecords(await res.json());
    } catch {
      setError('Failed to load Recycle Bin. Please try again.');
    } finally { setLoading(false); }
  }, [API_URL, navigate]);

  useEffect(() => { fetchTrash(); }, [fetchTrash]);

  const daysRemaining = (deletedAt) => {
    if (!deletedAt) return RETENTION_DAYS;
    const deleted = new Date(deletedAt);
    const expiry = new Date(deleted.getTime() + RETENTION_DAYS * 24 * 60 * 60 * 1000);
    return Math.max(0, Math.ceil((expiry - new Date()) / (24 * 60 * 60 * 1000)));
  };

  const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  const filtered = useMemo(() => records.filter((c) => {
    const t = searchTerm.toLowerCase();
    const matchSearch = !t ||
      c.full_name?.toLowerCase().includes(t) ||
      c.email?.toLowerCase().includes(t) ||
      c.mobile?.includes(searchTerm);
    const matchStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchSearch && matchStatus;
  }), [records, searchTerm, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const toggleSelect = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const allPageSelected = pageRows.length > 0 && pageRows.every((r) => selected.includes(r.id));
  const toggleSelectAllPage = () => {
    if (allPageSelected) setSelected((s) => s.filter((id) => !pageRows.some((r) => r.id === id)));
    else setSelected((s) => [...new Set([...s, ...pageRows.map((r) => r.id)])]);
  };

  const runAction = async (type, ids) => {
    setBusy(true);
    const endpoint = type === 'restore' ? 'restore' : 'delete-permanent';
    try {
      const res = await fetch(`${API_URL}/api/admin/consultations/${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids, admin_name: 'Admin' }),
      });
      if (res.ok) {
        setRecords((prev) => prev.filter((r) => !ids.includes(r.id)));
        setSelected((prev) => prev.filter((id) => !ids.includes(id)));
      }
    } catch { /* noop */ } finally {
      setBusy(false);
      setDialog(null);
    }
  };

  const dialogProps = () => {
    if (!dialog) return {};
    if (dialog.type === 'restore') {
      return {
        title: dialog.ids.length > 1 ? `Restore ${dialog.ids.length} records?` : 'Restore this consultation?',
        message: 'This will move the consultation (with all beneficiaries, notes and status) back to the active list.',
        confirmLabel: 'Restore', variant: 'warning',
      };
    }
    return {
      title: dialog.ids.length > 1 ? `Permanently delete ${dialog.ids.length} records?` : 'Permanently delete this consultation request?',
      message: 'This action cannot be undone. All beneficiary information, notes and related records will be permanently removed.',
      confirmLabel: 'Delete Permanently', variant: 'danger',
    };
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-trash-page">
      <header className="bg-white shadow-sm border-b border-[#E2E8F0] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/dashboard')} className="p-2 text-[#64748B] hover:text-[#1E88E5] hover:bg-[#F1F5F9] rounded-lg" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#1F2933] flex items-center gap-2"><Trash2 className="w-5 h-5 text-[#64748B]" /> Recycle Bin</h1>
              <p className="text-sm text-[#64748B]">Records are permanently deleted after {RETENTION_DAYS} days</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={fetchTrash} disabled={loading} className="p-2 text-[#64748B] hover:text-[#1E88E5] hover:bg-[#F1F5F9] rounded-lg" data-testid="trash-refresh-btn">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={async () => { try { await fetch(`${API_URL}/api/admin/logout`, { method: 'POST', credentials: 'include' }); } catch { /* noop */ } navigate('/admin'); }} className="flex items-center gap-2 px-4 py-2 text-[#64748B] hover:text-red-600 hover:bg-red-50 rounded-lg" data-testid="trash-logout-btn">
              <LogOut className="w-4 h-4" /><span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
              <input value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by name, email, or mobile…"
                className="w-full pl-10 pr-4 py-2.5 border border-[#CBD5E1] rounded-lg focus:ring-2 focus:ring-[#1E88E5] outline-none" data-testid="trash-search-input" />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="pl-10 pr-8 py-2.5 border border-[#CBD5E1] rounded-lg focus:ring-2 focus:ring-[#1E88E5] outline-none appearance-none bg-white min-w-[150px]" data-testid="trash-status-filter">
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="contacted">Contacted</option>
                <option value="enrolled">Enrolled</option>
                <option value="closed">Closed</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#94A3B8] pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Bulk toolbar */}
        <AnimatePresence>
          {selected.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="bg-[#1F2933] text-white rounded-xl p-3 mb-4 flex items-center justify-between" data-testid="bulk-toolbar">
              <span className="text-sm font-medium ml-2">{selected.length} selected</span>
              <div className="flex items-center gap-2">
                <button onClick={() => setDialog({ type: 'restore', ids: [...selected] })} className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#1E88E5] hover:bg-[#1976D2] rounded-lg text-sm font-semibold" data-testid="bulk-restore-btn">
                  <RotateCcw className="w-4 h-4" /> Restore
                </button>
                <button onClick={() => setDialog({ type: 'delete', ids: [...selected] })} className="inline-flex items-center gap-1.5 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-semibold" data-testid="bulk-delete-btn">
                  <Trash2 className="w-4 h-4" /> Delete Permanently
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-center gap-3"><AlertCircle className="w-5 h-5 text-red-500" /><p className="text-red-700">{error}</p></div>
        )}

        {loading ? (
          <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-12 text-center">
            <RefreshCw className="w-8 h-8 text-[#1E88E5] animate-spin mx-auto mb-4" /><p className="text-[#64748B]">Loading Recycle Bin…</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] p-12 text-center" data-testid="trash-empty-state">
            <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center mx-auto mb-4"><Trash2 className="w-8 h-8 text-[#94A3B8]" /></div>
            <h3 className="text-lg font-semibold text-[#1F2933] mb-2">Recycle Bin is empty</h3>
            <p className="text-[#64748B]">Deleted consultation requests will appear here for {RETENTION_DAYS} days.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-[#E2E8F0] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="trash-table">
                <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                  <tr>
                    <th className="px-4 py-4"><input type="checkbox" checked={allPageSelected} onChange={toggleSelectAllPage} className="w-4 h-4 rounded" data-testid="trash-select-all" /></th>
                    <th className="text-left px-4 py-4 text-sm font-semibold text-[#64748B]">Client</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold text-[#64748B]">Contact</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold text-[#64748B]">Consultation Date</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold text-[#64748B]">Moved to Trash</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold text-[#64748B]">Days Left</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold text-[#64748B]">Status</th>
                    <th className="text-left px-4 py-4 text-sm font-semibold text-[#64748B]">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]">
                  <AnimatePresence initial={false}>
                    {pageRows.map((c) => {
                      const dl = daysRemaining(c.deleted_at);
                      return (
                        <motion.tr key={c.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, backgroundColor: '#FEF2F2' }}
                          className="hover:bg-[#F8FAFC]" data-testid={`trash-row-${c.id}`}>
                          <td className="px-4 py-4"><input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggleSelect(c.id)} className="w-4 h-4 rounded" data-testid={`trash-select-${c.id}`} /></td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              <div className="w-9 h-9 bg-[#E2E8F0] rounded-full flex items-center justify-center"><User className="w-4 h-4 text-[#64748B]" /></div>
                              <div>
                                <p className="font-medium text-[#1F2933]">{c.full_name}</p>
                                <p className="text-xs text-[#64748B]">{c.age}y</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2 text-sm text-[#64748B]"><Phone className="w-3.5 h-3.5" />{c.mobile}</div>
                            {c.email && <div className="flex items-center gap-2 text-sm text-[#64748B]"><Mail className="w-3.5 h-3.5" /><span className="truncate max-w-[150px]">{c.email}</span></div>}
                          </td>
                          <td className="px-4 py-4 text-sm text-[#64748B]"><div className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{fmt(c.created_at)}</div></td>
                          <td className="px-4 py-4 text-sm text-[#64748B]"><div className="flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" />{fmt(c.deleted_at)}</div></td>
                          <td className="px-4 py-4">
                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${dl <= 5 ? 'bg-red-100 text-red-700' : 'bg-[#F1F5F9] text-[#64748B]'}`}>
                              <Clock className="w-3 h-3" />{dl} day{dl === 1 ? '' : 's'}
                            </span>
                          </td>
                          <td className="px-4 py-4"><span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(c.status)}`}>{c.status}</span></td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-1">
                              <button onClick={() => setDialog({ type: 'restore', ids: [c.id] })} className="p-2 text-[#1E88E5] hover:bg-[#E3F2FD] rounded-lg" title="Restore" data-testid={`restore-${c.id}`}><RotateCcw className="w-4 h-4" /></button>
                              <button onClick={() => setDialog({ type: 'delete', ids: [c.id] })} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Delete Permanently" data-testid={`delete-perm-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                            </div>
                          </td>
                        </motion.tr>
                      );
                    })}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E8F0]">
              <p className="text-sm text-[#64748B]">{filtered.length} record{filtered.length === 1 ? '' : 's'}</p>
              <div className="flex items-center gap-2">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="p-2 rounded-lg hover:bg-[#F1F5F9] disabled:opacity-40" data-testid="trash-prev-page"><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-sm text-[#64748B]">Page {page} / {totalPages}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="p-2 rounded-lg hover:bg-[#F1F5F9] disabled:opacity-40" data-testid="trash-next-page"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        )}
      </main>

      <ConfirmDialog
        open={!!dialog}
        {...dialogProps()}
        loading={busy}
        onCancel={() => setDialog(null)}
        onConfirm={() => runAction(dialog.type, dialog.ids)}
      />
    </div>
  );
};

export default AdminTrashPage;
